import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Image as ImageIcon, Loader2, Trash2 } from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerClose,
  DrawerTrigger,
} from '@/components/ui/drawer';

interface PhotoUploadSheetProps {
  /** URL da imagem atual (avatar/logo). Sem URL, mostra o `fallback`. */
  imageUrl?: string | null;
  /** Texto de recurso (iniciais) quando não há imagem. */
  fallback: string;
  /** Recebe o ficheiro escolhido. A validação vive no hook que o fornece. */
  onPick: (file: File) => void;
  /** Remove a imagem atual. */
  onRemove: () => void;
  busy?: boolean;
  /** Mostra a opção "Remover" no bottom sheet. */
  canRemove?: boolean;
  /** Tipos aceites no seletor de ficheiros. */
  accept?: string;
  ariaLabel?: string;
  /** Conteúdo centrado por baixo do avatar (ex.: nome). */
  caption?: ReactNode;
}

const DEFAULT_ACCEPT = 'image/png,image/jpeg,image/webp';

/**
 * UI partilhada de upload de foto/logo: avatar circular grande com selo de
 * câmara que abre um bottom sheet (tirar foto / escolher na galeria / remover).
 * Sem botões nem textos inline — a validação vive no hook que passa `onPick`.
 */
export function PhotoUploadSheet({
  imageUrl,
  fallback,
  onPick,
  onRemove,
  busy = false,
  canRemove = false,
  accept = DEFAULT_ACCEPT,
  ariaLabel,
  caption,
}: PhotoUploadSheetProps) {
  const { t } = useTranslation();
  const galeriaRef = useRef<HTMLInputElement>(null);
  const camaraRef = useRef<HTMLInputElement>(null);
  const [aberto, setAberto] = useState(false);

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite re-selecionar o mesmo ficheiro
    if (file) onPick(file);
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <Drawer open={aberto} onOpenChange={setAberto}>
        <DrawerTrigger asChild>
          <button
            type="button"
            className="relative rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            aria-label={ariaLabel ?? t('photo.change')}
            disabled={busy}
          >
            <span className="flex items-center justify-center overflow-hidden rounded-full h-24 w-24 bg-primary">
              {imageUrl ? (
                <img src={imageUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="text-primary-foreground font-bold text-3xl">{fallback}</span>
              )}
            </span>
            <span className="absolute bottom-0 right-0 flex items-center justify-center h-8 w-8 rounded-full bg-foreground text-background border-2 border-background">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            </span>
          </button>
        </DrawerTrigger>

        <DrawerContent>
          <div className="mx-auto w-full max-w-md p-4 pb-8 flex flex-col gap-2">
            <button
              type="button"
              className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 text-left text-foreground"
              onClick={() => {
                setAberto(false);
                camaraRef.current?.click();
              }}
            >
              <Camera className="h-5 w-5 shrink-0" />
              <span className="font-medium">{t('photo.takePhoto')}</span>
            </button>

            <button
              type="button"
              className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 text-left text-primary"
              onClick={() => {
                setAberto(false);
                galeriaRef.current?.click();
              }}
            >
              <ImageIcon className="h-5 w-5 shrink-0" />
              <span className="font-medium">{t('photo.fromGallery')}</span>
            </button>

            {canRemove && (
              <button
                type="button"
                className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 text-left text-destructive"
                onClick={() => {
                  setAberto(false);
                  onRemove();
                }}
              >
                <Trash2 className="h-5 w-5 shrink-0" />
                <span className="font-medium">{t('photo.remove')}</span>
              </button>
            )}

            <DrawerClose asChild>
              <button type="button" className="ow-btn ow-btn--ghost ow-btn--block mt-2">
                {t('common.cancel')}
              </button>
            </DrawerClose>
          </div>
        </DrawerContent>
      </Drawer>

      {caption}

      <input
        ref={galeriaRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={onFile}
      />
      <input
        ref={camaraRef}
        type="file"
        accept={accept}
        capture="environment"
        className="hidden"
        onChange={onFile}
      />
    </div>
  );
}
